/**
 * `/coach-portal`'s drawn-navy-TopBar decision (issue #767, PR #920 review
 * round 2, R1/R2) — kept in its own pure-JS module, no React, no `newUi()`
 * call baked in, for the same reason `lib/recordGate.js` gives (see that
 * file's own header): a plain function of its inputs is a fact a test can
 * pin without mounting a component, and `pages/CoachPortal.jsx` calls it
 * with the real `newUi(...)` reads and its own `rosterLoaded`/`players`
 * state at render time rather than duplicating the boolean arithmetic
 * inline.
 *
 * ============================================================
 * R1 — THE BUG THIS CLOSES: TWO WRITERS OF ONE FLAG
 * ============================================================
 * Round 1's fix moved the decision out of `CoachSquad.jsx` and into
 * `CoachPortal.jsx`, computed from the flag and the coach's role alone —
 * correct as far as it went, but `CoachPortalEmptyState.jsx` was STILL
 * separately calling `useRetireTextSize().setRetired(true)` on its own
 * mount and `(false)` on its own unmount. `RetireTextSizeContext.js`'s own
 * header names the hazard directly: "two screens both leaving it `true`
 * on unmount would fight each other." That is exactly what happened: a
 * coach with an empty roster (both `coach-portal-empty` and `squad` on)
 * accepts a pending invite IN PLACE — the roster goes from 0 to 1 without
 * a navigation — `CoachPortalEmptyState` unmounts, its cleanup fires
 * `setRetired(false)`, and nothing fires it back `true`: `CoachSquad`
 * (which now mounts instead) declares nothing itself any more (round 1's
 * own fix), so the drawn chrome silently reverts to the shipped header
 * until a reload. `CoachPortal.jsx` is now the ONE caller of `setRetired`
 * for this route — `CoachPortalEmptyState.jsx` declares nothing itself.
 *
 * ============================================================
 * R2 — TIE EACH FLAG TO THE SCREEN IT REBUILDS
 * ============================================================
 * Round 1's formula, `isCoach && (emptyOn || squadOn)`, drew the navy bar
 * over the SHIPPED page whenever EITHER flag was on, regardless of which
 * composition the roster actually produced — `coach-portal-empty` on
 * alone, roster populated (the shipped list renders, drawn bar over it);
 * `squad` on alone, empty roster (the shipped empty-roster card renders,
 * drawn bar over it). Both are the shipped screen wearing the rebuilt
 * screen's chrome.
 *
 * The orchestrator's ruling, encoded here:
 *   - BOTH flags on (the rollout configuration) — drawn from first paint,
 *     whatever the roster turns out to be. Whichever way the roster
 *     resolves, SOME rebuilt composition is going to render (the empty
 *     state or the squad), so there is no roster outcome under which
 *     showing the drawn bar early could be wrong. This is what keeps round
 *     1's own fix: the bar never has to wait on `rosterLoaded` at all when
 *     both are on, so the original swap-mid-interaction bug (F1) stays
 *     closed for the configuration that actually ships.
 *   - EXACTLY ONE flag on — the bar follows the roster once it is known:
 *     drawn for an empty roster with `coach-portal-empty` on, drawn for a
 *     populated roster with `squad` on, shipped otherwise. While the
 *     roster is still unknown (`rosterLoaded` false), it is SHIPPED, not
 *     drawn — a real, late swap the instant the roster resolves. This
 *     matches the single-flag behaviour already live on `main` today for
 *     `coach-portal-empty` alone (the ONLY single-flag combination any
 *     shipped code has ever run), and the orchestrator's ruling accepts
 *     it rather than widening this round's fix to a case F1 was never
 *     filed against.
 *   - NEITHER flag on, or NOT A COACH — never drawn. `isCoach` is checked
 *     first and short-circuits everything else: an admin viewing this
 *     route gets the shipped header regardless of either flag, because
 *     neither rebuilt composition is a screen an admin ever sees
 *     (`CoachPortal.jsx`'s own `showEmptyPortalRedraw`/`showSquadScreen`
 *     are both `isCoach`-gated the same way).
 *
 * @param {object} args
 * @param {boolean} args.isCoach
 * @param {boolean} args.emptyScreenOn — `newUi("coach-portal-empty")`.
 * @param {boolean} args.squadScreenOn — `newUi("squad")`.
 * @param {boolean} args.rosterLoaded — `CoachPortal.jsx`'s own `GET
 *   /players` state: false until that read has landed at least once.
 * @param {number} args.playerCount — `players.length`, meaningful only
 *   once `rosterLoaded` is true.
 * @returns {boolean}
 */
export function computeDrawnCoachChrome({ isCoach, emptyScreenOn, squadScreenOn, rosterLoaded, playerCount }) {
  if (!isCoach) return false;
  if (emptyScreenOn && squadScreenOn) return true;
  if (emptyScreenOn) return rosterLoaded && playerCount === 0;
  if (squadScreenOn) return rosterLoaded && playerCount > 0;
  return false;
}
